// RosterHistorySection.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import { DeleteIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { toastService } from "../../utils/ApiClient/toastService";

const STATUS_COLORS = {
  uploaded: "blue",
  validated: "purple",
  orchestration: "orange",
  completed: "green",
  active: "green",
  archived: "gray",
  inactive: "yellow",
};

const STATUS_STEP_MAP = {
  uploaded: "ANALYZING",
  validated: "MAPPING_REVIEW",
  orchestration: "PROCESSING",
  completed: "DONE",
};

function formatDate(timestamp) {
  if (!timestamp) return "";
  return new Date(Number(timestamp) * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const RosterHistorySection = ({
  importHistory,
  isLoading,
  error,
  deleteHistoryItem,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textSecondary = useColorModeValue("gray.500", "gray.400");
  const inputBg = useColorModeValue("white", "gray.700");
  const tileBg = useColorModeValue("white", "gray.800");
  const tileHover = useColorModeValue("blue.50", "gray.700");
  const tileBorder = useColorModeValue("gray.200", "gray.600");
  const tileActiveBorder = useColorModeValue("blue.300", "blue.500");
  const errorBg = useColorModeValue("red.50", "rgba(254,178,178,0.1)");

  const [selectedId, setSelectedId] = useState(null);

  // 🔍 SEARCH
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return importHistory.filter(
      (r) =>
        r.blobName?.toLowerCase().includes(q) ||
        r.createdBy?.toLowerCase().includes(q),
    );
  }, [search, importHistory]);

  // 🗑 DELETE
  const handleDelete = async (import_id, e) => {
    e.stopPropagation();
    try {
      const success = await deleteHistoryItem(import_id);
      if (success) {
        if (selectedId === import_id) {
          setSelectedId(null);
        }
        toastService.show({
          title: "Roster deleted",
          status: "success",
        });
      }
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  // ✅ SELECT
  const handleSelect = (roster) => {
    const targetStep = STATUS_STEP_MAP[roster.status];
    if (!targetStep) return;
    setSelectedId(roster.import_id);
    onSelect?.(roster, targetStep);
  };

  return (
    <Box p={4} border="1px solid" borderColor={borderColor} borderRadius="md">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="600" mb={4}>
          Roster Import History
        </Text>
      </Flex>

      {/* Search */}
      <InputGroup size="sm" mb={3}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color={textSecondary} boxSize="12px" />
        </InputLeftElement>
        <Input
          placeholder="Search by file or user..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg={inputBg}
          borderColor={borderColor}
          fontSize="13px"
          isDisabled={isLoading || !!error}
        />
      </InputGroup>

      {/* Content */}
      {isLoading ? (
        <Flex justify="center" py={8}>
          <Spinner size="sm" color="blue.400" />
        </Flex>
      ) : error ? (
        <Box
          bg={errorBg}
          border="1px solid"
          borderColor="red.200"
          borderRadius="md"
          px={4}
          py={3}
        >
          <Text fontSize="13px" fontWeight="600" color="red.500">
            Failed to load rosters
          </Text>
        </Box>
      ) : filtered.length === 0 ? (
        <Text fontSize="13px" color={textSecondary} textAlign="center" py={6}>
          {search ? "No rosters match your search." : "No rosters found."}
        </Text>
      ) : (
        <Flex direction="column" gap={2}>
          {filtered.map((roster) => {
            const isClickable = !!STATUS_STEP_MAP[roster.status];
            const isSelected = selectedId === roster.import_id;

            return (
              <Box
                key={roster.import_id}
                bg={tileBg}
                border="1px solid"
                borderColor={isSelected ? tileActiveBorder : tileBorder}
                borderRadius="md"
                px={4}
                py={3}
                cursor={isClickable ? "pointer" : "default"}
                onClick={() => isClickable && handleSelect(roster)}
                _hover={
                  isClickable
                    ? { bg: tileHover, borderColor: tileActiveBorder }
                    : {}
                }
                transition="all 0.15s ease"
                boxShadow={
                  isSelected
                    ? "0 0 0 2px var(--chakra-colors-blue-200)"
                    : "none"
                }
              >
                <Flex justify="space-between" align="center">
                  <Box flex={1} minW={0}>
                    <Text fontSize="13px" fontWeight="600" noOfLines={1}>
                      {roster.blobName}
                    </Text>
                    <Text
                      fontSize="11px"
                      color={textSecondary}
                      mt="2px"
                      noOfLines={1}
                    >
                      {roster.createdBy} · {formatDate(roster.createdAt)}
                    </Text>
                  </Box>

                  <Flex align="center" gap={2} ml={2} flexShrink={0}>
                    <Badge
                      colorScheme={STATUS_COLORS[roster.status] ?? "gray"}
                      fontSize="10px"
                      textTransform="capitalize"
                    >
                      {roster.status || "uploaded"}
                    </Badge>

                    <IconButton
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      aria-label="Delete roster"
                      onClick={(e) => handleDelete(roster.import_id, e)}
                    />
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

export default RosterHistorySection;
